import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Grid, IconButton, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router';
import { ProductServiceRoleConfiguration } from '../../components/productConfiguration/ProductServiceRoleConfiguration';
import AccordionGridWrapper from 'components/common/AccordionGridWrapper';
import BackToWorkflowOverviewIcon from 'assets/icons/return-workflow-overview-icon-blue.svg?react';
import { jbColors } from 'theme/jbColors';
import { AppDispatch, RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { selectBrUid } from 'selectors/accountSelector';
import StickyHeaderWrapper from 'components/common/StickyHeaderWrapper';
import { getStepMetadata } from 'utils/json-form/formDataUtils';
import { setFormData } from 'reducers/productConfigurationReducer';
import ConfigurationForm from 'components/productConfiguration/ConfigurationForm';
import RightSideBottomBar from 'components/common/RightSideBottomBar';
import { moveForwardServiceAgreement, updateServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import DeleteProductIcon from 'assets/icons/discard-draft-icon.svg?react';
import DeleteProductIconWhite from 'assets/icons/detelete-product-icon.svg?react';
import SaveProductIcon from 'assets/icons/save-product-icon.svg?react';
import SaveProductIconWhite from 'assets/icons/save-product-icon-white.svg?react';

import { setLoader } from 'reducers/appConfigReducer';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { callDeleteServiceRole } from 'services/service-role/callDeleteServiceRole';
import { callCreateServiceRole } from 'services/service-role/callCreateServiceRole';
import { ServiceAgreementV2, ConfigurationFormMetadata, ServiceRole } from 'pco-product-service-internal-consumer';
import { StepMetadataType } from 'types';

function ProductConfiguration() {
  const { processInstanceId } = useParams();
  const navigate = useNavigate();
  const brUid = useSelector(selectBrUid);
  const dispatch = useDispatch<AppDispatch>();
  const [isFormValid, setIsFormValid] = React.useState(true);
  const selectedServiceAgreement = useSelector(
    (state: RootState) => state.serviceAgreements.selectedServiceAgreement,
  ) as ServiceAgreementV2;
  const { formData } = useSelector((state: RootState) => state.productConfiguration) as {
    formData: ({ serviceRole?: { enabled?: string } } & Record<string, unknown>) | null;
  };
  const { loader } = useSelector((state: RootState) => state.appConfig);
  const [localServiceRole, setLocalServiceRole] = useState<ServiceRole | null>(null);
  const [localPresentServiceRole, setLocalPresentServiceRole] = useState<ServiceRole | null>(null);
  const [roleModified, setRoleModified] = useState(false);
  const [serviceRoleCreated, setServiceRoleCreated] = useState(false);
  const partnerNotFoundForServiceRole = useSelector((state: RootState) => state.partner.partnerNotFoundForServiceRole);
  const [hover, setHover] = useState<{ key: string; isHovering: boolean }>({
    key: '',
    isHovering: false,
  });

  const handleOnSaveDraft = async () => {
    const tempAgreement = { ...selectedServiceAgreement };
    const attributes = Array.isArray(tempAgreement?.attributes) ? [...tempAgreement.attributes] : [];
    const foundAttribute = attributes.find(
      (attribute) =>
        attribute.productAttributeId ===
        tempAgreement?.stepMetadata?.find((x) => x.stepMetadataType === StepMetadataType.CONFIGURATION_FORM_METADATA)
          ?.schemaUuid,
    );
    let attribute: { serviceAgreementAttributeId: string; productAttributeId: string; value: string } | undefined;

    if (localServiceRole && localServiceRole?.validFrom) {
      dispatch(
        setLoader({
          loading: true,
          message: 'Configuring service role...',
        }),
      );

      if (serviceRoleCreated && selectedServiceAgreement?.serviceDefinitionName !== 'ID Delegation') {
        if (localPresentServiceRole?.serviceRoleUid && roleModified) {
          const deleteResponse = await callDeleteServiceRole(localPresentServiceRole.serviceRoleUid);
          tempAgreement.serviceRoles = deleteResponse.serviceRoles;
        }

        const createResponse = await callCreateServiceRole(localServiceRole);
        if (createResponse) {
          tempAgreement.serviceRoles = createResponse.serviceRoles;
          setServiceRoleCreated(true);
          setRoleModified(false);
        }
      } else {
        const createResponse = await callCreateServiceRole(localServiceRole);
        if (createResponse) {
          tempAgreement.serviceRoles = createResponse.serviceRoles;
          setServiceRoleCreated(true);
          setRoleModified(false);
        }
      }
    }

    if (foundAttribute) {
      const tempItem = { ...foundAttribute };
      tempItem.value = JSON.stringify(formData);
      attributes.splice(0, attributes.length, tempItem);
    } else {
      const foundStep = tempAgreement?.stepMetadata?.find(
        (x) => x.stepMetadataType === StepMetadataType.CONFIGURATION_FORM_METADATA,
      );
      attribute = {
        serviceAgreementAttributeId: '',
        productAttributeId: foundStep?.schemaUuid ?? '',
        value: JSON.stringify(formData),
      };
      attributes.push(attribute);
    }

    if (tempAgreement) {
      tempAgreement.attributes = attributes;
    }

    setFormData(formData);

    try {
      await dispatch(updateServiceAgreement(tempAgreement, attributes));
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(
        setLoader({
          loading: false,
          message: '',
        }),
      );
    }
  };

  const handleConfigureProduct = async () => {
    dispatch(
      setLoader({
        loading: true,
        message: 'Configuring service role...',
      }),
    );

    await handleOnSaveDraft();
    if (!selectedServiceAgreement?.serviceAgreementId) {
      return;
    }

    const foundAttribute = selectedServiceAgreement?.stepMetadata?.find(
      (x) => x?.stepMetadataType === StepMetadataType.CONFIGURATION_FORM_METADATA,
    );
    const taskId = foundAttribute?.taskId;

    try {
      await dispatch(
        moveForwardServiceAgreement(taskId, selectedServiceAgreement.serviceAgreementId, undefined, undefined),
      );
    } catch (e) {
      console.log(e);
    } finally {
      navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
    }
  };

  const handleBack = () => {
    if (selectedServiceAgreement?.serviceAgreementId) {
      navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
    } else {
      navigate(`/accounts/${brUid}`);
    }
  };

  const disableRequestProduct = useMemo(() => {
    if (!formData) {
      console.log("There's no formData");
    }

    if (!isFormValid) {
      console.log('form is not valid, isFormValid is false');
      return true;
    }

    try {
      if (partnerNotFoundForServiceRole) {
        console.log('its partnerNotFoundForServiceRole');
        return false;
      }
      if (
        formData?.serviceRole?.enabled &&
        JSON.parse(formData?.serviceRole?.enabled) &&
        selectedServiceAgreement?.serviceRoles?.filter((sr) => sr.status !== 'DELETED' && sr.status !== 'WITHDRAWN')
          .length === 0 &&
        (!localServiceRole?.partnerUid || !localServiceRole?.validFrom)
      ) {
        console.log(`We will disable product.
         formData.serviceRole.enabled is ${formData?.serviceRole?.enabled}
         selectedServiceAgreement?.serviceRoles is ${selectedServiceAgreement?.serviceRoles}
         localServiceRole?.partnerUid is  ${localServiceRole?.partnerUid}
         localServiceRole?.validFrom is ${localServiceRole?.validFrom}
         `);
        return true;
      }

      return false;
    } catch (e) {
      console.log(e);
      return false;
    }
  }, [formData, isFormValid, localServiceRole, partnerNotFoundForServiceRole, selectedServiceAgreement?.serviceRoles]);

  const schema = useMemo<Record<string, unknown> | string | null>(() => {
    if (!selectedServiceAgreement) return null;
    const jsonSchema = (
      getStepMetadata(
        selectedServiceAgreement,
        StepMetadataType.CONFIGURATION_FORM_METADATA,
      ) as unknown as ConfigurationFormMetadata
    )?.schema;
    return jsonSchema ? JSON.parse(jsonSchema) : 'No schema found.';
  }, [selectedServiceAgreement]);

  useEffect(() => {
    if (!selectedServiceAgreement) return;
    const attributes = selectedServiceAgreement?.attributes ?? [];
    //set default form data
    const preConfiguredFormData = attributes[0]?.value;
    if (preConfiguredFormData) {
      dispatch(setFormData(JSON.parse(preConfiguredFormData)));
    }
  }, [processInstanceId, selectedServiceAgreement, dispatch]);

  useEffect(() => {
    if (
      selectedServiceAgreement?.serviceAgreementId &&
      selectedServiceAgreement?.agreementStatus !== 'READY_FOR_CONFIGURATION' &&
      selectedServiceAgreement?.agreementStatus !== 'IN_CONFIGURATION'
    ) {
      navigate(`/accounts/${brUid}/serviceAgreement/${selectedServiceAgreement?.serviceAgreementId}/details`);
    }
  }, [selectedServiceAgreement?.serviceAgreementId, navigate, brUid, selectedServiceAgreement?.agreementStatus]);

  const localSchema = schema && typeof schema !== 'string' ? schema : null;

  const formButtons = (
    <Grid
      item
      xs={12}
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        mr: 5,
      }}
    >
      <Button
        onMouseEnter={() => setHover({ key: 'discard', isHovering: true })}
        onMouseLeave={() => setHover({ key: '', isHovering: false })}
        sx={{
          backgroundColor: 'white',
          ':hover': {
            backgroundColor: jbColors.core.reflexBlue[100],
            color: 'white',
          },
        }}
        variant="outlined"
        onClick={() => handleBack()}
      >
        {hover.key === 'discard' && hover.isHovering ? (
          <DeleteProductIconWhite style={{ marginRight: '0.5em' }} />
        ) : (
          <DeleteProductIcon style={{ marginRight: '0.5em' }} />
        )}
        Discard Draft
      </Button>
      <Button
        sx={{
          backgroundColor: 'white',
          ':hover': {
            backgroundColor: jbColors.core.reflexBlue[100],
            color: 'white',
          },
        }}
        onMouseEnter={() => setHover({ key: 'save', isHovering: true })}
        onMouseLeave={() => setHover({ key: '', isHovering: false })}
        disabled={!formData}
        variant="outlined"
        onClick={() => handleOnSaveDraft()}
      >
        {hover.isHovering && hover.key === 'save' ? (
          <SaveProductIconWhite style={{ marginRight: '0.5em' }} />
        ) : (
          <SaveProductIcon style={{ marginRight: '0.5em' }} />
        )}
        Save Draft
      </Button>
      <Button disabled={disableRequestProduct} variant="contained" onClick={() => handleConfigureProduct()}>
        Request Product
      </Button>
    </Grid>
  );

  return (
    <StickyHeaderWrapper
      hasStickyBottomBar={true}
      side="right"
      topComponents={
        !loader.loading && (
          <Grid
            data-testid="product-configuration"
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              ml: 0.5,
              mb: 3,
              mt: 0.5,
            }}
          >
            <IconButton onClick={handleBack} sx={{ borderRadius: 0, p: 0 }}>
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 1,
                  ml: -0.6,
                  mb: -0.5,
                  width: 'fit-content',
                  border: `1px solid ${jbColors.core.stone[30]}`,
                  borderBottom: 'none',
                  backgroundColor: 'white',
                  ':hover': {
                    backgroundColor: jbColors.core.stone[40],
                    color: jbColors.core.reflexBlue[100],
                    border: `1px solid ${jbColors.core.stone[30]}`,
                  },
                }}
              >
                <Grid item xs={1}>
                  <BackToWorkflowOverviewIcon
                    style={{
                      display: 'flex',
                      color: jbColors.core.reflexBlue[100],
                      height: '1em',
                      width: '0.6em',
                      marginRight: '0.5em',
                    }}
                  />
                </Grid>
                <Grid item xs={11}>
                  <Typography variant={'body2'} sx={{ color: jbColors.core.reflexBlue[100] }}>
                    Back
                  </Typography>
                </Grid>
              </Grid>
            </IconButton>
          </Grid>
        )
      }
      centerComponents={
        loader.loading ? (
          <LoadingSpinner message={loader.message} />
        ) : (
          <>
            <AccordionGridWrapper
              expandedProp={selectedServiceAgreement?.serviceDefinitionName !== 'ID Delegation'}
              showExpandIcon={selectedServiceAgreement?.serviceDefinitionName !== 'ID Delegation'}
              title={selectedServiceAgreement?.serviceDefinitionName ?? ''}
            >
              {localSchema &&
                (selectedServiceAgreement?.serviceDefinitionName === 'ID Delegation' ? (
                  <ConfigurationForm
                    formValues={undefined}
                    propsUiSchema={undefined}
                    setIsFormValid={undefined}
                    schema={localSchema}
                  />
                ) : (
                  <ConfigurationForm
                    formValues={undefined}
                    propsUiSchema={undefined}
                    setIsFormValid={setIsFormValid}
                    schema={localSchema}
                  />
                ))}
            </AccordionGridWrapper>
            {formData && formData.serviceRole && formData?.serviceRole?.enabled && (
              <AccordionGridWrapper title={'Service Roles'}>
                {selectedServiceAgreement && (
                  <ProductServiceRoleConfiguration
                    setRoleModified={setRoleModified}
                    setServiceRoleCreated={setServiceRoleCreated}
                    setLocalServiceRole={setLocalServiceRole}
                    setLocalPresentServiceRole={setLocalPresentServiceRole}
                  />
                )}
              </AccordionGridWrapper>
            )}
            {!loader.loading && <RightSideBottomBar>{formButtons}</RightSideBottomBar>}
          </>
        )
      }
    />
  );
}

export default ProductConfiguration;
